import { resolveIntlTimeZoneId } from './resolveIntlTimeZone';

export type UtcFormatStyle =
  | 'full'
  | 'date'
  | 'time'
  | 'datetime'
  /** Short month + day + year (learner dashboard recent activity). */
  | 'activityDate'
  /** 12-hour clock with minutes (learner dashboard recent activity). */
  | 'activityTime'
  /** Date and time with millisecond precision (admin action path). */
  | 'datetimeMs';

function parseFractionToMs(fractionDigits: string | undefined): number {
  if (!fractionDigits) return 0;
  const ms = Math.round(parseFloat(`0.${fractionDigits}`) * 1000);
  return Math.min(999, Math.max(0, ms));
}

/** Parse API UTC timestamps (often without a Z suffix) into a Date. */
export function parseUtcDateString(utcDateString: string): Date | null {
  if (utcDateString === null || utcDateString === undefined || utcDateString === '') {
    return null;
  }

  const dateString = String(utcDateString).trim();
  const hasTimezone =
    dateString.endsWith('Z') ||
    /[+-]\d{2}:?\d{2}$/.test(dateString) ||
    /[+-]\d{4}$/.test(dateString);

  try {
    if (!hasTimezone) {
      if (dateString.includes('T')) {
        const isoMatch = dateString.match(
          /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/
        );
        if (isoMatch) {
          const [, year, month, day, hour, minute, second, fraction] = isoMatch;
          const ms = parseFractionToMs(fraction);
          const utcDate = new Date(
            Date.UTC(
              parseInt(year, 10),
              parseInt(month, 10) - 1,
              parseInt(day, 10),
              parseInt(hour, 10),
              parseInt(minute, 10),
              parseInt(second, 10),
              ms
            )
          );
          return isNaN(utcDate.getTime()) ? null : utcDate;
        }
        const withZ = new Date(`${dateString}Z`);
        return isNaN(withZ.getTime()) ? null : withZ;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        return isNaN(utcDate.getTime()) ? null : utcDate;
      }
      const parsed = new Date(dateString);
      if (!isNaN(parsed.getTime())) return parsed;
      const withZ = new Date(`${dateString}Z`);
      return isNaN(withZ.getTime()) ? null : withZ;
    }
    const parsed = new Date(dateString);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

function toPersianDigits(input: string): string {
  return input.replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)] ?? d);
}

function formatOptionsForStyle(format: UtcFormatStyle, tz: string, isFa: boolean): Intl.DateTimeFormatOptions {
  const calendar: Intl.DateTimeFormatOptions = isFa ? { calendar: 'gregory' } : {};
  switch (format) {
    case 'full':
      return {
        ...calendar,
        timeZone: tz,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
        timeZoneName: 'short'
      };
    case 'date':
      return { ...calendar, timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' };
    case 'time':
      return {
        ...calendar,
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      };
    case 'activityDate':
      return { ...calendar, timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' };
    case 'activityTime':
      return {
        ...calendar,
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: !isFa
      };
    case 'datetime':
    default:
      return {
        ...calendar,
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      };
  }
}

/**
 * Format a UTC timestamp in the given IANA (or Windows-mapped) time zone.
 */
export function formatUtcInTimeZone(
  utcDateString: string,
  timeZoneId: string | undefined | null,
  format: UtcFormatStyle = 'datetime',
  locale?: string
): string {
  const utcDate = parseUtcDateString(utcDateString);
  if (!utcDate) {
    if (utcDateString === null || utcDateString === undefined || utcDateString === '') {
      return 'N/A';
    }
    return 'Invalid date';
  }

  const isFa = (locale ?? '').toLowerCase().startsWith('fa');
  const intlLocale = isFa ? 'fa-IR' : 'en-US';
  const timezone = resolveIntlTimeZoneId(timeZoneId);
  const formatInZone = (tz: string): string => {
    const formatted = new Intl.DateTimeFormat(intlLocale, formatOptionsForStyle(format, tz, isFa)).format(utcDate);
    return isFa ? toPersianDigits(formatted) : formatted;
  };

  try {
    return formatInZone(timezone);
  } catch {
    try {
      return formatInZone('UTC');
    } catch {
      return 'Invalid date';
    }
  }
}
